import { useState, useEffect } from 'react';
import { getTeamMembers, createTeamMember, updateTeamMember, deleteTeamMember, uploadTeamPhoto, getMyAgencies } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Trash2, Pencil, Users, Mail, Phone, Copy, Check, Camera, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const TeamPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [credentialsDialog, setCredentialsDialog] = useState(null);
  const [copied, setCopied] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', password: 'changeme123', phone: '', agency_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [agencies, setAgencies] = useState([]);

  useEffect(() => {
    fetchMembers();
    getMyAgencies().then(res => setAgencies(res.data)).catch(() => {});
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await getTeamMembers();
      setMembers(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', email: '', password: 'changeme123', phone: '', agency_id: agencies[0]?.id || '' });
    setDialogOpen(true);
  };

  const openEdit = (member) => {
    setEditing(member);
    setFormData({ name: member.name || '', email: member.email || '', password: '', phone: member.phone || '', agency_id: member.agency_id || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { name: formData.name, email: formData.email, phone: formData.phone, agency_id: formData.agency_id || null };
        if (formData.password) payload.password = formData.password;
        await updateTeamMember(editing.id, payload);
        toast.success('Collaborateur modifié');
        setDialogOpen(false);
        setEditing(null);
      } else {
        await createTeamMember(formData);
        toast.success('Collaborateur créé !');
        setCredentialsDialog({ email: formData.email, password: formData.password });
        setDialogOpen(false);
      }
      setFormData({ name: '', email: '', password: 'changeme123', phone: '' });
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} de l'équipe ?`)) return;
    try {
      await deleteTeamMember(id);
      toast.success('Collaborateur supprimé');
      fetchMembers();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handlePhotoUpload = async (memberId, file) => {
    try {
      await uploadTeamPhoto(memberId, file);
      toast.success('Photo mise à jour');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload photo');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) {
    return (
      <div className="space-y-5" data-testid="team-loading">
        <Skeleton className="h-10 w-48 bg-white" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 bg-white" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="team-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#191D1E]">Mon équipe</h1>
          <p className="text-sm text-[#687175] mt-1">{members.length} collaborateur(s)</p>
        </div>
        <Button className="btn-primary" onClick={openCreate} data-testid="add-member-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un collaborateur
        </Button>
      </div>

      {members.length === 0 ? (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-12 text-center">
          <Users className="w-12 h-12 text-[#BFC6CC] mx-auto mb-4" />
          <h3 className="font-bold text-[#191D1E] mb-1">Pas encore de collaborateurs</h3>
          <p className="text-sm text-[#687175]">
            Ajoutez des membres à votre équipe pour qu'ils puissent gérer vos clients et alertes.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {members.map((m, index) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white border border-[#BFC6CC]/12 rounded-2xl px-5 py-4 flex items-center justify-between"
              data-testid={`team-member-${m.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="relative group">
                  {m.photo_path ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/photos/${m.photo_path}`}
                      alt={m.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#397DFF]/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-[#397DFF]">
                        {m.name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                    <Camera className="w-4 h-4 text-white" />
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                      if (e.target.files[0]) handlePhotoUpload(m.id, e.target.files[0]);
                    }} data-testid={`photo-upload-${m.id}`} />
                  </label>
                </div>
                <div>
                  <p className="font-bold text-[#191D1E]">{m.name}</p>
                  <div className="flex items-center gap-4 text-sm text-[#687175]">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {m.email}</span>
                    {m.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {m.phone}</span>}
                    {m.agency_id && agencies.find(a => a.id === m.agency_id) && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {agencies.find(a => a.id === m.agency_id)?.name}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openEdit(m)}
                  className="p-2 text-[#397DFF] hover:bg-[#397DFF]/10 rounded-lg transition-colors"
                  title="Modifier"
                  data-testid={`edit-member-${m.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(m.id, m.name)}
                  className="p-2 text-[#939BA0] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Supprimer"
                  data-testid={`delete-member-${m.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null); }}>
        <DialogContent className="bg-white border-[#BFC6CC]/30">
          <DialogHeader>
            <DialogTitle className="text-[#191D1E]">
              {editing ? 'Modifier le collaborateur' : 'Nouveau collaborateur'}
            </DialogTitle>
            <DialogDescription className="text-[#687175]">
              {editing ? 'Modifiez les informations du collaborateur.' : 'Renseignez les informations du nouveau collaborateur.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-[#191D1E]">Nom complet</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="Jean Dupont" required data-testid="member-name-input" />
            </div>
            <div>
              <Label className="text-[#191D1E]">Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="collaborateur@exemple.com" required data-testid="member-email-input" />
            </div>
            <div>
              <Label className="text-[#191D1E]">
                {editing ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}
              </Label>
              <Input value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder={editing ? '••••••••' : 'changeme123'}
                required={!editing} data-testid="member-password-input" />
            </div>
            <div>
              <Label className="text-[#191D1E]">Téléphone (optionnel)</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="06 00 00 00 00" data-testid="member-phone-input" />
            </div>
            {agencies.length > 0 && (
              <div>
                <Label className="text-[#191D1E]">Agence de rattachement</Label>
                <select value={formData.agency_id}
                  onChange={(e) => setFormData({ ...formData, agency_id: e.target.value })}
                  className="w-full mt-1.5 p-2.5 rounded-lg bg-[#F5F6F9] border border-[#BFC6CC] text-[#191D1E] text-sm focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF] outline-none"
                  data-testid="member-agency-select">
                  <option value="">— Aucune agence —</option>
                  {agencies.map(a => (
                    <option key={a.id} value={a.id}>{a.name} — {a.city}</option>
                  ))}
                </select>
              </div>
            )}
            <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="member-submit-btn">
              {submitting ? 'En cours...' : (editing ? 'Enregistrer' : 'Créer le collaborateur')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials dialog */}
      <Dialog open={!!credentialsDialog} onOpenChange={() => setCredentialsDialog(null)}>
        <DialogContent className="bg-white border-[#BFC6CC]/30">
          <DialogHeader>
            <DialogTitle className="text-[#191D1E]">Identifiants créés</DialogTitle>
            <DialogDescription className="text-[#687175]">
              Communiquez ces identifiants à votre collaborateur. Il pourra se connecter immédiatement.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div className="bg-[#F5F6F9] rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-[#687175]">Email</p>
                <p className="font-mono text-[#191D1E]">{credentialsDialog?.email}</p>
              </div>
              <button onClick={() => copyToClipboard(credentialsDialog?.email, 'email')} className="p-2 hover:bg-white rounded-lg transition-colors">
                {copied === 'email' ? <Check className="w-4 h-4 text-[#10D183]" /> : <Copy className="w-4 h-4 text-[#687175]" />}
              </button>
            </div>
            <div className="bg-[#F5F6F9] rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-[#687175]">Mot de passe</p>
                <p className="font-mono text-[#191D1E]">{credentialsDialog?.password}</p>
              </div>
              <button onClick={() => copyToClipboard(credentialsDialog?.password, 'password')} className="p-2 hover:bg-white rounded-lg transition-colors">
                {copied === 'password' ? <Check className="w-4 h-4 text-[#10D183]" /> : <Copy className="w-4 h-4 text-[#687175]" />}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
